import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Check, Sparkles, Crown, Zap, Loader2, ShieldCheck, Code2, Brain,
  ArrowRight, Infinity as InfinityIcon, Award, Star, Clock, GraduationCap
} from "lucide-react";
import { getStoredUser, getAuthConfig, loginWithGoogle } from "../lib/userAuth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PRODUCT_META = {
  python_lab: {
    title: "Python Lab",
    tagline: "30 modules · 600 lessons · AI tutor",
    icon: Code2,
    accent: "#00F0FF",
    accentTo: "#0070FF",
    sectionId: "python-lab",
  },
  seo: {
    title: "SEO Services Suite",
    tagline: "46-point audits · AI fixes · PDF reports",
    icon: ShieldCheck,
    accent: "#FF8AB8",
    accentTo: "#FF3DA0",
    sectionId: "seo",
  },
  sop_studio: {
    title: "SOP Studio Pro",
    tagline: "Edit history · Impact analysis · Unlimited SOPs",
    icon: Brain,
    accent: "#7EE787",
    accentTo: "#43A047",
    sectionId: "sop-studio",
  },
  tutor: {
    title: "ShansoTutor — AI Tuition Teacher",
    tagline: "Classes 6–12 · CBSE & TN Board · Talks & listens · Tamil voice",
    icon: GraduationCap,
    accent: "#22D3EE",
    accentTo: "#7000FF",
    sectionId: "tutor",
  },
  bundle: {
    title: "ShansoAI Pro Mega-Pass",
    tagline: "Every tool we build · One subscription",
    icon: Crown,
    accent: "#FFC857",
    accentTo: "#FF9500",
    sectionId: "bundle",
  },
};

function loadCashfreeIfNeeded(callback) {
  if (window.Cashfree) { callback(); return; }
  const s = document.createElement("script");
  s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
  s.onload = callback;
  document.body.appendChild(s);
}

function PriceTag({ amount, suffix, original, accent }) {
  return (
    <div className="flex items-baseline gap-1.5 flex-wrap">
      {original && original !== amount && (
        <span className="text-zinc-500 line-through text-sm">₹{original.toLocaleString("en-IN")}</span>
      )}
      <span className="text-4xl font-bold text-white">₹{amount.toLocaleString("en-IN")}</span>
      {suffix && <span className="text-zinc-400 text-sm" style={{ color: accent }}>{suffix}</span>}
    </div>
  );
}

function PlanCard({ plan, billingCycle, currentSub, onBuy, loading, recommended, accent, accentTo }) {
  const isOneTime = plan.is_one_time;
  const price = billingCycle === "annual" ? plan.annual_price : plan.monthly_price;
  const suffix = isOneTime ? " one-time" : (billingCycle === "annual" ? "/year" : "/month");
  const isCurrent = currentSub?.plan_id === plan.id;
  const isFreePlan = plan.monthly_price === 0;
  const seatsOut = plan.seats_total && plan.seats_remaining === 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className={`relative rounded-2xl p-6 border ${recommended
        ? "border-[color:var(--accent)] shadow-[0_0_40px_-12px_var(--accent)]"
        : "border-zinc-800/60"} bg-zinc-900/40`}
      style={{ "--accent": accent }}
      data-testid={`plan-card-${plan.id}`}
    >
      {recommended && (
        <span className="absolute -top-3 left-6 px-3 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase"
              style={{ background: `linear-gradient(90deg, ${accent}, ${accentTo})`, color: "#000" }}>
          ★ Recommended
        </span>
      )}
      {plan.limited_seats && plan.seats_remaining > 0 && (
        <span className="absolute -top-3 right-6 px-3 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase bg-amber-500/20 border border-amber-500/40 text-amber-300">
          <Clock size={10} className="inline mr-1" /> {plan.seats_remaining} of {plan.seats_total} left
        </span>
      )}
      {seatsOut && (
        <span className="absolute -top-3 right-6 px-3 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase bg-zinc-800 border border-zinc-700 text-zinc-500">
          Sold out
        </span>
      )}

      <h3 className="text-white font-bold text-xl">{plan.name}</h3>
      <div className="mt-3">
        {price === 0 ? (
          <div className="flex items-baseline gap-1">
            <span className="text-4xl font-bold text-white">Free</span>
          </div>
        ) : (
          <PriceTag amount={price} suffix={suffix} accent={accent} />
        )}
      </div>

      <ul className="mt-5 space-y-2 min-h-[140px]">
        {(plan.features || []).map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
            <Check size={14} className="mt-0.5 shrink-0" style={{ color: accent }} />
            <span className="leading-relaxed">{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6">
        {isCurrent ? (
          <div className="w-full text-center py-3 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-sm font-semibold" data-testid={`plan-current-${plan.id}`}>
            <Award size={14} className="inline mr-1.5 -mt-0.5" /> Current plan
          </div>
        ) : isFreePlan ? (
          <button disabled className="w-full py-3 rounded-lg bg-zinc-800 text-zinc-500 text-sm font-semibold cursor-default">
            Always free
          </button>
        ) : seatsOut ? (
          <button disabled className="w-full py-3 rounded-lg bg-zinc-800 text-zinc-600 text-sm font-semibold cursor-not-allowed">
            Sold out
          </button>
        ) : (
          <button
            onClick={() => onBuy(plan)}
            disabled={loading}
            className="w-full py-3 rounded-lg text-black font-bold text-sm transition-opacity hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2"
            style={{ background: `linear-gradient(90deg, ${accent}, ${accentTo})` }}
            data-testid={`plan-buy-${plan.id}`}
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <>Get {plan.name} <ArrowRight size={14} /></>}
          </button>
        )}
      </div>
    </motion.div>
  );
}

function ProductSection({ product, plans, currentSub, onBuy, loadingPlanId, allowAnnualToggle = true }) {
  const [billing, setBilling] = useState("monthly");
  const meta = PRODUCT_META[product];
  if (!meta) return null;
  const Icon = meta.icon;

  // For Python Lab (one-time only), no toggle
  const showToggle = allowAnnualToggle && plans.some(p => !p.is_one_time);
  const savePlan = plans.find(p => !p.is_one_time && p.monthly_price > 0 && p.annual_price > 0);
  const annualSave = savePlan ? Math.round((1 - savePlan.annual_price / (savePlan.monthly_price * 12)) * 100) : 17;
  const recommendedId = product === "python_lab" ? "pylab_pro"
                      : product === "tutor" ? "tutor_plus"
                      : product === "seo" ? "seo_pro"
                      : product === "sop_studio" ? "sop_studio_annual"
                      : product === "bundle" ? "bundle_annual"
                      : null;

  return (
    <section id={meta.sectionId} className="py-14 scroll-mt-24" data-testid={`section-${product}`}>
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Icon size={18} style={{ color: meta.accent }} />
            <span className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: meta.accent }}>{meta.tagline}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white">{meta.title}</h2>
        </div>
        {showToggle && (
          <div className="inline-flex bg-zinc-900 border border-zinc-800 rounded-full p-1" data-testid={`billing-toggle-${product}`}>
            <button onClick={() => setBilling("monthly")}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition ${billing === "monthly" ? "bg-zinc-800 text-white" : "text-zinc-400 hover:text-zinc-200"}`}
              data-testid={`bill-monthly-${product}`}>Monthly</button>
            <button onClick={() => setBilling("annual")}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition ${billing === "annual" ? "bg-zinc-800 text-white" : "text-zinc-400 hover:text-zinc-200"}`}
              data-testid={`bill-annual-${product}`}>
              Annual <span style={{ color: meta.accent }}>−{annualSave}%</span>
            </button>
          </div>
        )}
      </div>

      <div className={`grid gap-5 ${plans.length === 2 ? "md:grid-cols-2" : plans.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2 lg:grid-cols-4"}`}>
        {plans.map((p) => (
          <PlanCard
            key={p.id}
            plan={p}
            billingCycle={billing}
            currentSub={currentSub}
            onBuy={(plan) => onBuy(plan, billing)}
            loading={loadingPlanId === p.id}
            recommended={p.id === recommendedId}
            accent={meta.accent}
            accentTo={meta.accentTo}
          />
        ))}
      </div>
    </section>
  );
}

/* ─── ROOT PAGE ─── */
export default function PricingPage() {
  const [plans, setPlans] = useState([]);
  const [mySubs, setMySubs] = useState([]);
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [params] = useSearchParams();
  const orderId = params.get("sub_order");
  const navigate = useNavigate();
  const user = getStoredUser();

  useEffect(() => {
    (async () => {
      try {
        const [plansRes, subsRes] = await Promise.all([
          axios.get(`${API}/subscriptions/plans`),
          user ? axios.get(`${API}/subscriptions/my-subscriptions`, getAuthConfig())
               : Promise.resolve({ data: { subscriptions: [] } }),
        ]);
        setPlans(plansRes.data || []);
        setMySubs(subsRes.data?.subscriptions || []);
      } catch (e) { console.error(e); }
    })();
  }, [user?.user_id]);

  // After Cashfree redirect, verify the order
  useEffect(() => {
    if (!orderId || !user) return;
    (async () => {
      try {
        const res = await axios.post(`${API}/subscriptions/verify-payment`,
          { order_id: orderId }, getAuthConfig());
        if (res.data.status === "paid") {
          // Refresh subscriptions
          const subsRes = await axios.get(`${API}/subscriptions/my-subscriptions`, getAuthConfig());
          setMySubs(subsRes.data?.subscriptions || []);
          // Strip the query param so refresh doesn't re-verify
          window.history.replaceState({}, "", "/pricing");
        }
      } catch (e) { console.error("verify failed", e); }
    })();
  }, [orderId, user?.user_id]);

  const subFor = (product) => mySubs.find(s => s.product === product || s.product === "bundle");

  const handleBuy = async (plan, billingCycle) => {
    if (!user) {
      loginWithGoogle();
      return;
    }
    if (loadingPlanId) return;
    setLoadingPlanId(plan.id);
    try {
      const res = await axios.post(`${API}/subscriptions/create-order`, {
        plan_id: plan.id,
        billing_cycle: plan.is_one_time ? "monthly" : billingCycle,
        origin_url: window.location.origin,
      }, getAuthConfig());

      const { payment_session_id } = res.data;
      loadCashfreeIfNeeded(() => {
        const cashfree = window.Cashfree({ mode: "production" });
        cashfree.checkout({
          paymentSessionId: payment_session_id,
          redirectTarget: "_self",
        });
      });
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Could not create order. Try again.";
      alert(typeof msg === "string" ? msg : JSON.stringify(msg));
      setLoadingPlanId(null);
    }
  };

  const groupedPlans = (product) => plans.filter(p => p.product === product);
  const pylabPlans = groupedPlans("python_lab");
  const seoPlans = [...groupedPlans("seo")];  // we'll prepend a Free synthetic card
  const sopPlans = groupedPlans("sop_studio");
  const bundlePlans = groupedPlans("bundle");
  const tutorPlans = groupedPlans("tutor");
  const tutorFreeSynth = {
    id: "tutor_free", product: "tutor", name: "Free", monthly_price: 0, annual_price: 0,
    is_one_time: true,
    features: [
      "1 subject · 5 teacher replies a day",
      "Tap-to-listen teacher voice · 10 min/day",
      "1 practice quiz a day · streaks & badges",
      "2-day Plus trial for every new student",
    ],
  };

  // Synthetic "Free" cards for SEO & Python Lab (so the page shows the entry tier visually)
  const sopFreeSynth = {
    id: "sop_free", product: "sop_studio", name: "Free", monthly_price: 0, annual_price: 0,
    is_one_time: true,
    features: [
      "Generate SOPs from text · PDF · DOCX · audio · video",
      "Full toolkit: Checklist · Mermaid Flow · Risk · Training · Automation",
      "Save SOPs to your library (view-only)",
      "Sign in to upgrade for editing",
    ],
  };

  // Synthetic "Free" cards for SEO & Python Lab (so the page shows the entry tier visually)
  const seoFreeSynth = {
    id: "seo_free", product: "seo", name: "Free", monthly_price: 0, annual_price: 0,
    is_one_time: true,
    features: [
      "1 audit/day (signed-in)",
      "5 AI generations/day (Meta · Schema · Keywords · Optimizer)",
      "View results, Google SERP & social previews",
      "Sign-in to upgrade",
    ],
  };
  const pylabFreeSynth = {
    id: "pylab_free", product: "python_lab", name: "Free", monthly_price: 0, annual_price: 0,
    is_one_time: true,
    features: [
      "Module 1 — 20 lessons",
      "AI tutor (5 hints/day)",
      "Public showcase",
      "Try a free lesson — no signup",
    ],
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="border-b border-zinc-800/60 bg-zinc-950/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-zinc-300 hover:text-white text-sm font-semibold flex items-center gap-2" data-testid="back-home">
            <Brain size={16} className="text-[#00F0FF]" /> ShansoAI
          </Link>
          <nav className="hidden md:flex items-center gap-1 text-xs">
            <a href="#python-lab" className="px-3 py-1.5 rounded-full text-zinc-400 hover:text-white hover:bg-zinc-900" data-testid="nav-pylab">Python Lab</a>
            <a href="#seo" className="px-3 py-1.5 rounded-full text-zinc-400 hover:text-white hover:bg-zinc-900" data-testid="nav-seo">SEO Suite</a>
            <a href="#sop-studio" className="px-3 py-1.5 rounded-full text-zinc-400 hover:text-white hover:bg-zinc-900" data-testid="nav-sop">SOP Studio</a>
            <a href="#tutor" className="px-3 py-1.5 rounded-full text-zinc-400 hover:text-white hover:bg-zinc-900" data-testid="nav-tutor">AI Tutor</a>
            <a href="#bundle" className="px-3 py-1.5 rounded-full text-zinc-400 hover:text-white hover:bg-zinc-900" data-testid="nav-bundle">Mega-Pass</a>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 pt-16 pb-6 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] text-[11px] font-medium mb-5">
          <Sparkles size={11} /> Simple, transparent pricing
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
          Pay once.<br />
          <span className="bg-gradient-to-r from-[#00F0FF] via-[#7000FF] to-[#FF3DA0] bg-clip-text text-transparent">Build forever.</span>
        </h1>
        <p className="text-zinc-400 text-base mt-5 max-w-xl mx-auto">
          Lifetime Python Lab access for the price of a single bootcamp month.
          SEO Suite plans for indie devs, agencies, and growth teams.
          Or grab the Mega-Pass and unlock everything.
        </p>
        {mySubs.length > 0 && (
          <div className="mt-7 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs" data-testid="active-subs-banner">
            <Award size={12} /> You have {mySubs.length} active plan{mySubs.length > 1 ? "s" : ""} —
            {mySubs.map((s, i) => <span key={s.order_id} className="font-semibold ml-1">{s.plan_name}{i < mySubs.length - 1 ? "," : ""}</span>)}
          </div>
        )}
      </section>

      <main className="max-w-6xl mx-auto px-4 pb-20">
        {/* Python Lab */}
        <ProductSection
          product="python_lab"
          plans={[pylabFreeSynth, ...pylabPlans]}
          currentSub={subFor("python_lab")}
          onBuy={handleBuy}
          loadingPlanId={loadingPlanId}
          allowAnnualToggle={false}
        />

        {/* SEO Suite */}
        <ProductSection
          product="seo"
          plans={[seoFreeSynth, ...seoPlans]}
          currentSub={subFor("seo")}
          onBuy={handleBuy}
          loadingPlanId={loadingPlanId}
        />

        {/* SOP Studio */}
        <ProductSection
          product="sop_studio"
          plans={[sopFreeSynth, ...sopPlans]}
          currentSub={subFor("sop_studio")}
          onBuy={handleBuy}
          loadingPlanId={loadingPlanId}
        />

        {/* ShansoTutor */}
        <ProductSection
          product="tutor"
          plans={[tutorFreeSynth, ...tutorPlans]}
          currentSub={subFor("tutor")}
          onBuy={handleBuy}
          loadingPlanId={loadingPlanId}
        />

        {/* Bundle */}
        <ProductSection
          product="bundle"
          plans={bundlePlans}
          currentSub={subFor("bundle")}
          onBuy={handleBuy}
          loadingPlanId={loadingPlanId}
        />

        {/* FAQ section */}
        <section className="py-14 max-w-3xl mx-auto" data-testid="pricing-faq">
          <h3 className="text-2xl font-bold text-white mb-6 text-center">Common questions</h3>
          <div className="space-y-3">
            {[
              { q: "Is the Python Lab Pro Pass really lifetime?", a: "Yes. One payment, lifetime access to all 30 modules and all future lessons we add to the curriculum." },
              { q: "Can I switch from SEO Starter to Pro?", a: "Yes — let your Starter plan run out, then subscribe to Pro. Or contact support@shansoai.com to upgrade mid-cycle with prorated credit." },
              { q: "What does the Mega-Pass include?", a: "Python Lab Pro+ (all 600 lessons), SEO Suite Pro (unlimited audits + AI generations), and access to every paid feature in our future tools — at one price." },
              { q: "Refunds?", a: "All sales are final — no refunds. Every paid plan grants instant lifetime/digital access, so please try the free tier first and review the curriculum + sample certificate before purchasing. Read the full Refund Policy for the rare technical-issue exceptions (e.g. duplicate Cashfree charges)." },
              { q: "Currency & taxes?", a: "All prices in INR (₹). GST is included. Cashfree handles payment processing securely (UPI, cards, net banking, wallets)." },
            ].map((f, i) => (
              <details key={i} className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4 group" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer text-white font-medium text-sm flex items-center gap-2">
                  <Star size={14} className="text-[#00F0FF] group-open:rotate-180 transition-transform" />
                  {f.q}
                </summary>
                <p className="mt-3 text-zinc-400 text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Footer CTA */}
        <section className="py-10 text-center">
          <p className="text-zinc-500 text-sm">
            Have questions? Email <a href="mailto:support@shansoai.com" className="text-[#00F0FF] hover:underline">support@shansoai.com</a>
          </p>
        </section>
      </main>
    </div>
  );
}
